import { useState, useEffect, useCallback } from "react"
import type { PageCursor, UserProfile } from "@ezbatna/core/domain/models"
import type { AdminContactMessage, ContactMessageStatus } from "@ezbatna/core"
import { getContainer } from "../lib/container"
import { Button, ConfirmDialog, DataTable, StatusPill, useToast, type DataTableColumn } from "../components/ui"

const PAGE_SIZE = 20

const statusConfig: Record<ContactMessageStatus, { label: string; variant: "warning" | "neutral" | "success" }> = {
  new: { label: "جديد", variant: "warning" },
  read: { label: "تمت القراءة", variant: "neutral" },
  resolved: { label: "تم الحل", variant: "success" },
}

const preferredContactLabel: Record<string, string> = {
  email: "البريد الإلكتروني",
  phone: "الهاتف",
}

type BulkAction = "mark-read" | "close-all" | "delete-all"

const bulkCopy: Record<BulkAction, { title: string; description: string; confirmLabel: string; destructive: boolean }> = {
  "mark-read": {
    title: "تحديد كل الرسائل الجديدة كمقروءة؟",
    description: "سيتم تحويل كل رسالة بحالة «جديد» إلى «تمت القراءة».",
    confirmLabel: "تحديد الكل كمقروء",
    destructive: false,
  },
  "close-all": {
    title: "إغلاق كل الرسائل المفتوحة؟",
    description: "سيتم تحويل كل رسالة غير محلولة (جديد أو تمت القراءة) إلى «تم الحل».",
    confirmLabel: "إغلاق الكل",
    destructive: false,
  },
  "delete-all": {
    title: "حذف كل رسائل التواصل نهائياً؟",
    description: "سيتم حذف جميع رسائل التواصل من قاعدة البيانات بشكل نهائي ولا يمكن التراجع عنه.",
    confirmLabel: "حذف الكل نهائياً",
    destructive: true,
  },
}

export function SupportPage() {
  const [messages, setMessages] = useState<AdminContactMessage[]>([])
  const [senders, setSenders] = useState<Record<string, UserProfile>>({})
  const [stats, setStats] = useState<Record<ContactMessageStatus, number> | null>(null)
  const [cursor, setCursor] = useState<PageCursor | null>(null)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null)
  const [bulkLoading, setBulkLoading] = useState(false)
  const toast = useToast()

  const resolveSenders = useCallback(async (list: AdminContactMessage[]) => {
    const ids = Array.from(new Set(list.map((m) => m.userId))).filter((id) => id && !senders[id])
    if (ids.length === 0) return
    const profiles = await Promise.all(ids.map((id) => getContainer().userProfileRepository.fetchProfile(id)))
    setSenders((prev) => {
      const next = { ...prev }
      profiles.forEach((p, i) => {
        if (p) next[ids[i]!] = p
      })
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchStats = async () => {
    setStats(await getContainer().contactRepository.countByStatus())
  }

  const fetchMessages = async (after?: PageCursor | null) => {
    setLoading(true)
    try {
      const page = await getContainer().contactRepository.fetchPage(PAGE_SIZE, after)
      setMessages((prev) => (after ? [...prev, ...page.items] : page.items))
      setCursor(page.nextCursor)
      resolveSenders(page.items)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMessages()
    fetchStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateStatus = async (message: AdminContactMessage, status: ContactMessageStatus) => {
    setBusyId(message.id)
    try {
      await getContainer().contactRepository.updateStatus(message.id, status)
      setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, status } : m)))
      fetchStats()
      toast.show("تم تحديث حالة الرسالة", "success")
    } catch (e) {
      toast.show(`خطأ: ${(e as Error).message}`, "danger")
    } finally {
      setBusyId(null)
    }
  }

  const runBulkAction = async () => {
    if (!bulkAction) return
    setBulkLoading(true)
    try {
      const repo = getContainer().contactRepository
      if (bulkAction === "mark-read") await repo.markAllAsRead()
      else if (bulkAction === "close-all") await repo.closeAll()
      else await repo.deleteAll()

      toast.show("تم تنفيذ الإجراء بنجاح", "success")
      setMessages([])
      setCursor(null)
      await Promise.all([fetchMessages(), fetchStats()])
    } catch (e) {
      toast.show(`خطأ: ${(e as Error).message}`, "danger")
    } finally {
      setBulkLoading(false)
      setBulkAction(null)
    }
  }

  const columns: DataTableColumn<AdminContactMessage>[] = [
    {
      key: "title",
      header: "الموضوع والرسالة",
      render: (m) => (
        <div className="max-w-[420px]">
          <p className="font-semibold">{m.title}</p>
          <p className="whitespace-pre-wrap text-caption text-text-secondary">{m.body}</p>
        </div>
      ),
    },
    {
      key: "sender",
      header: "المرسل",
      render: (m) => {
        const sender = senders[m.userId]
        return (
          <div>
            <p>{m.userName || "—"}</p>
            {sender?.username && <p className="text-caption text-accent">@{sender.username}</p>}
            <p className="text-caption text-text-secondary">{m.userEmail}</p>
          </div>
        )
      },
    },
    {
      key: "preferredContact",
      header: "طريقة التواصل المفضلة",
      render: (m) => (
        <div>
          <p>{preferredContactLabel[m.preferredContact] ?? m.preferredContact}</p>
          <p className="text-caption text-text-secondary">{m.contactValue}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "الحالة",
      render: (m) => {
        const status = m.status ?? "new"
        const config = statusConfig[status]
        return <StatusPill variant={config.variant}>{config.label}</StatusPill>
      },
    },
    {
      key: "createdAt",
      header: "التاريخ",
      align: "end",
      render: (m) => <span className="text-caption text-text-secondary">{m.createdAt?.toLocaleDateString("ar-SA") ?? "—"}</span>,
    },
    {
      key: "actions",
      header: "إجراءات",
      align: "center",
      render: (m) => {
        const status = m.status ?? "new"
        const isBusy = busyId === m.id
        return (
          <div className="flex flex-wrap items-center justify-center gap-xs">
            {status === "new" && (
              <Button variant="secondary" size="sm" loading={isBusy} onClick={() => updateStatus(m, "read")}>
                تمت القراءة
              </Button>
            )}
            {status !== "resolved" && (
              <Button variant="primary" size="sm" loading={isBusy} onClick={() => updateStatus(m, "resolved")}>
                إغلاق كمحلول
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="flex flex-col gap-lg">
      <div>
        <h1 className="text-display font-bold text-text-primary">الرسائل</h1>
        <p className="mt-2xs text-body-sm text-text-secondary">رسائل التواصل الواردة من المستخدمين</p>
      </div>

      <div className="flex flex-wrap items-center gap-md">
        <StatusPill variant="warning">جديد: {stats?.new ?? "…"}</StatusPill>
        <StatusPill variant="neutral">تمت القراءة: {stats?.read ?? "…"}</StatusPill>
        <StatusPill variant="success">تم الحل: {stats?.resolved ?? "…"}</StatusPill>

        <div className="ms-auto flex flex-wrap items-center gap-xs">
          <Button variant="secondary" size="sm" onClick={() => setBulkAction("mark-read")}>
            تحديد الكل كمقروء
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setBulkAction("close-all")}>
            إغلاق الكل
          </Button>
          <Button variant="destructive" size="sm" onClick={() => setBulkAction("delete-all")}>
            حذف الكل
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={messages}
        rowKey={(m) => m.id}
        loading={loading}
        emptyTitle="لا توجد رسائل"
        emptyDescription="لم يتم استلام أي رسائل تواصل حتى الآن."
      />

      {cursor && (
        <Button variant="secondary" onClick={() => fetchMessages(cursor)} loading={loading} className="self-start">
          عرض المزيد
        </Button>
      )}

      <ConfirmDialog
        open={!!bulkAction}
        title={bulkAction ? bulkCopy[bulkAction].title : ""}
        description={bulkAction ? bulkCopy[bulkAction].description : undefined}
        confirmLabel={bulkAction ? bulkCopy[bulkAction].confirmLabel : undefined}
        destructive={bulkAction ? bulkCopy[bulkAction].destructive : undefined}
        loading={bulkLoading}
        onConfirm={runBulkAction}
        onCancel={() => setBulkAction(null)}
      />
    </div>
  )
}
